import { Controller } from "@hotwired/stimulus"

// « @ » in a staff message's text box: lists the staff whose name starts with what follows (« @mar » → Marie DURAND,
// accents and case aside), under that « @ », ↑ ↓ to move, Entrée or Tab to pick, Échap to close. Picking writes
// « @Prénom NOM » in the text, which the server reads as a mention (StaffMessages::SyncMentionsService). The names
// written are tinted, as in a posted message: a text box styles none of its text, so a see-through copy of it laid
// over it (same font, padding and scroll, text invisible) holds the tint.
export default class extends Controller {
  static targets = ["input", "menu"]
  static values = { users: Array }
  static classes = ["active"]

  connect () {
    this.matches = []
    this.activeIndex = 0
    this.buildHighlights()
    this.highlight()
  }

  disconnect () {
    this.resizeObserver?.disconnect()
    this.inputTarget.removeEventListener("scroll", this.onScroll)
    this.highlights?.remove()
  }

  update () {
    this.highlight()
    const query = this.query()
    this.matches = query === null ? [] : this.usersValue.filter((user) => matches(user.name, query)).slice(0, 6)
    this.activeIndex = 0
    this.render()
    if (this.isOpen()) this.placeUnder(this.inputTarget.selectionStart - query.length - 1)
  }

  navigate (event) {
    if (!this.isOpen()) return

    switch (event.key) {
      case "ArrowDown":
        this.move(event, 1)
        break
      case "ArrowUp":
        this.move(event, -1)
        break
      case "Enter":
      case "Tab":
        if (event.metaKey || event.ctrlKey) return
        event.preventDefault()
        event.stopPropagation()
        this.insert(this.matches[this.activeIndex])
        break
      case "Escape":
        event.preventDefault()
        event.stopPropagation()
        this.close()
        break
    }
  }

  // mousedown, so the text box keeps the focus.
  pick (event) {
    event.preventDefault()
    this.insert(this.matches[Number(event.currentTarget.dataset.index)])
  }

  close () {
    this.matches = []
    this.render()
  }

  // What follows the last « @ » before the caret, when it starts a word: null when there is none.
  query () {
    const { value, selectionStart } = this.inputTarget
    const match = value.slice(0, selectionStart).match(/(?:^|\s)@([^\s@]*(?: [^\s@]*)?)$/)
    return match ? match[1] : null
  }

  move (event, step) {
    event.preventDefault()
    this.activeIndex = (this.activeIndex + step + this.matches.length) % this.matches.length
    this.render()
  }

  // A space after the name, unless the text after the caret starts with one.
  insert (user) {
    const input = this.inputTarget
    const after = input.value.slice(input.selectionStart)
    const space = /^\s/.test(after) ? "" : " "
    const before = input.value.slice(0, input.selectionStart).replace(/@([^\s@]*(?: [^\s@]*)?)$/, `@${user.name}${space}`)
    input.value = before + after
    input.setSelectionRange(before.length, before.length)
    input.dispatchEvent(new Event("input", { bubbles: true }))
    this.close()
  }

  isOpen () {
    return this.matches.length > 0
  }

  // The see-through copy over the text box, kept on it as it grows (textarea-autogrow) and scrolls.
  buildHighlights () {
    const input = this.inputTarget
    this.highlights = document.createElement("div")
    this.highlights.className = "admin-staff-message-mentions-highlights"
    this.highlights.setAttribute("aria-hidden", "true")
    input.after(this.highlights)

    this.onScroll = () => { this.highlights.scrollTop = input.scrollTop }
    input.addEventListener("scroll", this.onScroll)
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.placeHighlights())
      this.resizeObserver.observe(input)
    }
  }

  // The text, its staff names in a <mark> (the tint), on top of the text box.
  highlight () {
    if (!this.highlights) return

    this.highlights.innerHTML = `${highlightedHtml(this.inputTarget.value, this.usersValue.map((user) => user.name))}\n`
    this.placeHighlights()
  }

  placeHighlights () {
    const input = this.inputTarget
    const style = window.getComputedStyle(input)
    MIRRORED_STYLES.forEach((property) => { this.highlights.style[property] = style[property] })
    Object.assign(this.highlights.style, {
      top: `${input.offsetTop}px`,
      left: `${input.offsetLeft}px`,
      width: `${input.offsetWidth}px`,
      height: `${input.offsetHeight}px`
    })
    this.highlights.scrollTop = input.scrollTop
  }

  // The list right under the « @ » at +position+, kept inside the text box's container.
  placeUnder (position) {
    const input = this.inputTarget
    const menu = this.menuTarget
    const container = menu.offsetParent || input.parentElement
    const caret = caretCoordinates(input, position)
    const inputBox = input.getBoundingClientRect()
    const containerBox = container.getBoundingClientRect()
    const left = inputBox.left - containerBox.left + caret.left - input.scrollLeft
    const top = inputBox.top - containerBox.top + caret.top - input.scrollTop + caret.height

    menu.style.left = `${Math.max(0, Math.min(left, container.clientWidth - menu.offsetWidth))}px`
    menu.style.top = `${top}px`
  }

  render () {
    this.menuTarget.hidden = !this.isOpen()
    this.menuTarget.replaceChildren(...this.matches.map((user, index) => this.item(user, index)))
  }

  item (user, index) {
    const item = document.createElement("li")
    const button = document.createElement("button")
    button.type = "button"
    button.textContent = `@${user.name}`
    button.dataset.index = index
    button.dataset.action = "mousedown->admin--staff-message-mentions#pick"
    button.setAttribute("aria-selected", String(index === this.activeIndex))
    if (index === this.activeIndex) button.classList.add(...this.activeClasses)
    item.append(button)
    return item
  }
}

// Copied from the text box onto a hidden copy, so its text wraps the same way.
const MIRRORED_STYLES = [
  "boxSizing", "width", "borderTopWidth", "borderRightWidth", "borderBottomWidth", "borderLeftWidth", "borderStyle",
  "paddingTop", "paddingRight", "paddingBottom", "paddingLeft", "fontFamily", "fontSize", "fontStyle", "fontWeight",
  "fontVariant", "letterSpacing", "lineHeight", "textTransform", "textIndent", "wordSpacing", "tabSize"
]

// Where the character at +position+ starts in +textarea+, from its border box's top left, and its line's height: a
// hidden copy of the box holds the text before it, then a marker.
function caretCoordinates (textarea, position) {
  const style = window.getComputedStyle(textarea)
  const mirror = document.createElement("div")
  MIRRORED_STYLES.forEach((property) => { mirror.style[property] = style[property] })
  Object.assign(mirror.style, { position: "absolute", top: "0", left: "-9999px", visibility: "hidden", whiteSpace: "pre-wrap", overflowWrap: "break-word" })
  mirror.textContent = textarea.value.slice(0, position)
  const marker = document.createElement("span")
  marker.textContent = textarea.value.slice(position) || "."
  mirror.append(marker)
  document.body.append(mirror)

  const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.2
  const coordinates = {
    top: marker.offsetTop + (parseFloat(style.borderTopWidth) || 0),
    left: marker.offsetLeft + (parseFloat(style.borderLeftWidth) || 0),
    height: lineHeight || 0
  }
  mirror.remove()
  return coordinates
}

function escapeHtml (text) {
  return text.replace(/[&<>"']/g, (character) => `&#${character.charCodeAt(0)};`)
}

// +text+, escaped, each « @Prénom NOM » of +names+ in a <mark>, longest names first (« @Marie DURAND-ROY » before
// « @Marie DURAND »), whole names only.
function highlightedHtml (text, names) {
  const escaped = escapeHtml(text)
  if (names.length === 0) return escaped

  const alternatives = [...names].sort((a, b) => b.length - a.length).map((name) => escapeHtml(name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
  const pattern = new RegExp(`@(?:${alternatives.join("|")})(?![\\p{L}\\p{N}-])`, "gu")
  return escaped.replace(pattern, (mention) => `<mark>${mention}</mark>`)
}

function normalize (text) {
  return text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
}

// « mar » matches Marie DURAND and Paul MARTIN: the name, or one of its words, starts with the query.
function matches (name, query) {
  const normalizedName = normalize(name)
  const normalizedQuery = normalize(query)
  return normalizedName.startsWith(normalizedQuery) || normalizedName.split(" ").some((word) => word.startsWith(normalizedQuery))
}
